import { useEffect, useRef } from 'react';
import { useAuth } from 'auth';
import { useLocationService } from 'services/location';
import { checkTokenExpiration } from 'utils/token';
import { useTranslation } from 'react-i18next';

const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes
const CRITICAL_THRESHOLD = 1 * 60 * 1000; // 1 minute
const INITIAL_DELAY = 1000; // 1 seconde
const CHECK_INTERVAL = 5000; // 3 secondes

export const useTokenExpirationMonitor = (enabled = true) => {
	const { t } = useTranslation();
	const authContext = useAuth();
	const location = useLocationService();
	const timeoutRef = useRef<NodeJS.Timeout>();
	const isMountedRef = useRef(true);

	useEffect(() => {
		if (!enabled) {
			return;
		}

		isMountedRef.current = true;

		const checkTokenExpiry = async () => {
			if (!isMountedRef.current) return;

			try {
				if (!authContext?.getToken) {
					timeoutRef.current = setTimeout(checkTokenExpiry, 1000);
					return;
				}

				const token = await authContext.getToken();
				if (!token) {
					console.log(
						'[Token Monitor] Expired or unavailable token, reloading page...'
					);
					// `navigate(getHref())` was a no-op — the browser ignores
					// same-URL `href =` assignments. Use the dedicated reload
					// method to actually force a full page reload.
					location.reload();
					return;
				}

				const tokenInfo = checkTokenExpiration(token);
				if (!tokenInfo) {
					console.log(
						'[Token Monitor] Unable to decode token, retrying...'
					);
					timeoutRef.current = setTimeout(checkTokenExpiry, 1000);
					return;
				}

				console.log(
					'[Token Monitor] Expiration:',
					tokenInfo.expirationDate.toLocaleString()
				);
				console.log(
					'[Token Monitor] Remaining time:',
					Math.floor(tokenInfo.timeUntilExpiration / 1000),
					'secondes'
				);

				if (tokenInfo.timeUntilExpiration <= 0) {
					console.log(
						'[Token Monitor] Expired token, reloading page...'
					);
					location.reload();
					return;
				}

				// Déterminer l'intervalle de vérification en fonction du temps restant
				let nextCheckDelay;
				if (tokenInfo.timeUntilExpiration <= CRITICAL_THRESHOLD) {
					// Dans la dernière minute, vérifier toutes les 3 secondes
					nextCheckDelay = CHECK_INTERVAL;
				} else if (
					tokenInfo.timeUntilExpiration <= TOKEN_REFRESH_THRESHOLD
				) {
					// Dans les 5 dernières minutes, vérifier toutes les 10 secondes
					nextCheckDelay = 10000;
				} else {
					// Sinon, programmer le prochain check pour 5 minutes avant l'expiration
					nextCheckDelay =
						tokenInfo.timeUntilExpiration - TOKEN_REFRESH_THRESHOLD;
				}

				timeoutRef.current = setTimeout(
					checkTokenExpiry,
					nextCheckDelay
				);
			} catch (error) {
				console.error(
					'[Token Monitor] Erreur lors de la vérification du token:',
					error
				);
				timeoutRef.current = setTimeout(checkTokenExpiry, 1000);
			}
		};

		const timer = setTimeout(() => {
			checkTokenExpiry();
		}, INITIAL_DELAY);

		// Background tabs have `setTimeout` throttled to ~1/min by Chromium —
		// the scheduled check (10 s / 5 s near expiration) doesn't fire on
		// time when the user comes back from a long idle. Re-trigger the
		// check on `visibilitychange` so an expired token gets detected
		// within ~1 s of the tab regaining focus, not 1 min later.
		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible' && isMountedRef.current) {
				if (timeoutRef.current) clearTimeout(timeoutRef.current);
				checkTokenExpiry();
			}
		};
		document.addEventListener('visibilitychange', onVisibilityChange);

		return () => {
			isMountedRef.current = false;
			clearTimeout(timer);
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			document.removeEventListener('visibilitychange', onVisibilityChange);
		};
	}, [authContext, location, t, enabled]);

	return {
		isTokenExpiring: false
	};
};
